import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { ShowcaseContent, ShowcaseProps } from "../types.ts";
import { useKeyboardNav } from "../hooks/useKeyboardNav.ts";
import { HelixDefs } from "./Helix.tsx";
import { DizzarooLogo, IconChevLeft, IconChevRight, IconClose, IconGrid, IconPlay } from "./icons.tsx";
import { HeroSlide } from "./slides/HeroSlide.tsx";
import { ProblemSlide } from "./slides/ProblemSlide.tsx";
import { ApproachSlide } from "./slides/ApproachSlide.tsx";
import { MarqueeSlide } from "./slides/MarqueeSlide.tsx";
import { AllCapsSlide } from "./slides/AllCapsSlide.tsx";
import { PlatformFitSlide } from "./slides/PlatformFitSlide.tsx";
import { DifferentiatorsSlide } from "./slides/DifferentiatorsSlide.tsx";
import { WhatsNextSlide } from "./slides/WhatsNextSlide.tsx";
import { pad2 } from "../utils.tsx";

interface SlideEntry {
  title: string;
  node: ReactNode;
  preview: ReactNode;
}

function buildSlides(
  content: ShowcaseContent,
  appKey: string | undefined,
  showHelix: boolean,
  refs: React.MutableRefObject<(HTMLElement | null)[]>,
  jumpToTitle: (title: string) => void
): SlideEntry[] {
  const entries: SlideEntry[] = [];
  const setRef = (i: number) => (el: HTMLElement | null) => {
    refs.current[i] = el;
  };

  const noop = () => {};

  let idx = 0;
  entries.push({
    title: "Overview",
    node: <HeroSlide key="hero" ref={setRef(idx++)} content={content} showHelix={showHelix} onJumpTo={jumpToTitle} />,
    preview: <HeroSlide key="prev-hero" ref={null} content={content} showHelix={false} onJumpTo={noop} />,
  });
  entries.push({
    title: "Problem",
    node: <ProblemSlide key="problem" ref={setRef(idx++)} index={entries.length + 1} problem={content.problem} />,
    preview: <ProblemSlide key="prev-problem" ref={null} index={entries.length + 1} problem={content.problem} />,
  });
  entries.push({
    title: "Approach",
    node: <ApproachSlide key="approach" ref={setRef(idx++)} index={entries.length + 1} approach={content.approach} />,
    preview: <ApproachSlide key="prev-approach" ref={null} index={entries.length + 1} approach={content.approach} />,
  });

  const marquees = (content.capabilities || []).filter((c) => c.marquee);
  if (marquees.length) {
    entries.push({
      title: "Critical Abilities",
      node: <MarqueeSlide key="marquee" ref={setRef(idx++)} index={entries.length + 1} capabilities={marquees} />,
      preview: <MarqueeSlide key="prev-marquee" ref={null} index={entries.length + 1} capabilities={marquees} />,
    });
  }

  const rest = (content.capabilities || []).filter((c) => !c.marquee);
  if (rest.length) {
    entries.push({
      title: "Full Surface",
      node: <AllCapsSlide key="allcaps" ref={setRef(idx++)} index={entries.length + 1} capabilities={rest} />,
      preview: <AllCapsSlide key="prev-allcaps" ref={null} index={entries.length + 1} capabilities={rest} />,
    });
  }

  entries.push({
    title: "Platform Fit",
    node: <PlatformFitSlide key="platform" ref={setRef(idx++)} index={entries.length + 1} appKey={appKey} platformFit={content.platformFit} />,
    preview: <PlatformFitSlide key="prev-platform" ref={null} index={entries.length + 1} appKey={appKey} platformFit={content.platformFit} />,
  });

  if (content.differentiators && content.differentiators.length > 0) {
    entries.push({
      title: "Differentiators",
      node: <DifferentiatorsSlide key="diff" ref={setRef(idx++)} index={entries.length + 1} differentiators={content.differentiators} />,
      preview: <DifferentiatorsSlide key="prev-diff" ref={null} index={entries.length + 1} differentiators={content.differentiators} />,
    });
  }

  if (content.whatsNext && content.whatsNext.length > 0) {
    entries.push({
      title: "What's next",
      node: <WhatsNextSlide key="next" ref={setRef(idx++)} index={entries.length + 1} whatsNext={content.whatsNext} />,
      preview: <WhatsNextSlide key="prev-next" ref={null} index={entries.length + 1} whatsNext={content.whatsNext} />,
    });
  }

  return entries;
}

export function Showcase({ content, appKey, showHelix = true, onSlideChange }: ShowcaseProps) {
  const [active, setActive] = useState(0);
  const [present, setPresent] = useState(false);
  const [overviewOpen, setOverviewOpen] = useState(false);

  const slideRefs = useRef<(HTMLElement | null)[]>([]);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const slides = useMemo(() => {
    slideRefs.current = [];
    return buildSlides(content, appKey, showHelix, slideRefs, (t) => jumpToTitleRef.current(t));
  }, [content, appKey, showHelix]);

  const titles = useMemo(() => slides.map((s) => s.title), [slides]);

  const goTo = useCallback(
    (i: number) => {
      const clamped = Math.max(0, Math.min(titles.length - 1, i));
      setActive(clamped);
      onSlideChange?.(clamped, titles[clamped]);

      if (present) {
        window.scrollTo({ top: 0 });
      } else {
        const sec = slideRefs.current[clamped];
        if (sec) {
          const off = sec.getBoundingClientRect().top + window.scrollY - 70;
          window.scrollTo({ top: off, behavior: "smooth" });
        }
      }
    },
    [titles, present, onSlideChange]
  );

  const jumpToTitle = useCallback(
    (title: string) => {
      const i = titles.indexOf(title);
      if (i >= 0) goTo(i);
    },
    [titles, goTo]
  );

  const jumpToTitleRef = useRef(jumpToTitle);
  useEffect(() => {
    jumpToTitleRef.current = jumpToTitle;
  }, [jumpToTitle]);

  const next = useCallback(() => goTo(active + 1), [goTo, active]);
  const prev = useCallback(() => goTo(active - 1), [goTo, active]);

  const togglePresent = useCallback(
    (force?: boolean) => {
      const should = force === undefined ? !present : force;
      setPresent(should);
      if (!should) window.scrollTo({ top: 0, behavior: "smooth" });
    },
    [present]
  );

  useKeyboardNav({
    onNext: next,
    onPrev: prev,
    onTogglePresent: () => togglePresent(),
    onEscape: () => {
      if (overviewOpen) {
        setOverviewOpen(false);
      } else if (present) {
        togglePresent(false);
      } else {
        setOverviewOpen(true);
      }
    },
    isPresent: present,
    isOverviewOpen: overviewOpen,
  });

  useEffect(() => {
    document.body.classList.toggle("dz-present", present);
    return () => document.body.classList.remove("dz-present");
  }, [present]);

  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) e.target.classList.add("dz-in-view");
        }
      },
      { threshold: 0.2 }
    );
    slideRefs.current.forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, [slides]);

  useLayoutEffect(() => {
    const first = slideRefs.current[0];
    if (first) requestAnimationFrame(() => first.classList.add("dz-in-view"));
  }, [slides]);

  useEffect(() => {
    if (present) return;
    let timer: number | undefined;
    function onScroll() {
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        const mid = window.scrollY + window.innerHeight * 0.35;
        let curr = 0;
        for (let i = 0; i < slideRefs.current.length; i++) {
          const el = slideRefs.current[i];
          if (el && el.offsetTop <= mid) curr = i;
        }
        if (curr !== active) {
          setActive(curr);
          onSlideChange?.(curr, titles[curr]);
        }
      }, 60);
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (timer) window.clearTimeout(timer);
    };
  }, [present, active, titles, onSlideChange]);

  useEffect(() => {
    if (present) return;
    function onScroll() {
      slideRefs.current.forEach((el) => {
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const center = rect.top + rect.height / 2 - window.innerHeight / 2;
        el.style.transform = `translateY(${center * 0.04}px)`;
      });
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      slideRefs.current.forEach((el) => { if (el) el.style.transform = ""; });
    };
  }, [present, slides]);

  useEffect(() => {
    setActive(0);
    window.scrollTo({ top: 0 });
  }, [content]);

  useEffect(() => {
    slideRefs.current.forEach((el, i) => {
      if (!el) return;
      el.classList.toggle("dz-active", i === active);
    });
  }, [active, slides]);

  return (
    <div className="dz-showcase" ref={rootRef}>
      <HelixDefs />

      <header className="dz-top-chrome">
        <div className="dz-top-inner">
          <div className="dz-brand" aria-label="Dizzaroo Showcase">
            <span className="dz-logo-mark" aria-hidden="true">
              <DizzarooLogo />
            </span>
            <span className="dz-brand-name">
              Di<span className="dz-brand-name__zz">zz</span>aroo
              <span className="dz-brand-name__label">
                Showcase · <span>{content.app.name}</span>
              </span>
            </span>
          </div>
          <div className="dz-chrome-actions">
            <button
              type="button"
              className="dz-chrome-btn"
              onClick={() => setOverviewOpen((v) => !v)}
              title="Overview (Esc)"
            >
              <IconGrid />
              Overview
            </button>
            <button
              type="button"
              className="dz-chrome-btn dz-chrome-btn--primary"
              onClick={() => togglePresent(true)}
              title="Present (P)"
            >
              <IconPlay />
              Present
            </button>
          </div>
        </div>
      </header>

      <main className="dz-stage">{slides.map((s) => s.node)}</main>

      <nav className="dz-rail" aria-label="Slide navigation">
        {titles.map((title, i) => (
          <button
            type="button"
            key={i}
            className={i === active ? "dz-rail__btn dz-rail__btn--active" : "dz-rail__btn"}
            onClick={() => goTo(i)}
            aria-label={title}
          >
            <span className="dz-rail__tip">{title}</span>
          </button>
        ))}
      </nav>

      <div className="dz-present-controls">
        <button type="button" className="dz-pc-btn" onClick={prev} title="Previous (←)">
          <IconChevLeft />
        </button>
        <span className="dz-pc-count">
          {pad2(active + 1)} / {pad2(titles.length)}
        </span>
        <button type="button" className="dz-pc-btn" onClick={next} title="Next (→)">
          <IconChevRight />
        </button>
        <button type="button" className="dz-pc-btn" onClick={() => togglePresent(false)} title="Exit (Esc)">
          <IconClose />
        </button>
      </div>

      <div className={overviewOpen ? "dz-overview dz-overview--open" : "dz-overview"}>
        <h2>Overview · Click any slide to jump</h2>
        <div className="dz-ov-grid">
          {slides.map((slide, i) => (
            <div
              key={i}
              className="dz-ov-card"
              onClick={() => {
                setOverviewOpen(false);
                goTo(i);
              }}
            >
              <div className="dz-ov-card__preview" aria-hidden="true">
                {slide.preview}
              </div>
              <div className="dz-ov-card__overlay" />
              <div className="dz-ov-card__n">SLIDE {pad2(i + 1)}</div>
              <div className="dz-ov-card__title">{titles[i]}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

